/**
 * ReviewDecisionModal - TASK-374: CO Review System
 *
 * Modal for submitting a review decision: approve, request changes, or reject.
 * Enforces that all sections must be reviewed before a decision can be submitted.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X,
  CheckCircle,
  AlertTriangle,
  XCircle,
  ShieldAlert,
  Loader2,
} from 'lucide-react';
import type { ReviewDecision, RejectionCategory } from '../../../types/complianceWorkflow';

interface ReviewDecisionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (decision: ReviewDecision, notes: string, category?: RejectionCategory) => void;
  allSectionsReviewed: boolean;
}

const REJECTION_CATEGORIES: { value: RejectionCategory; label: string }[] = [
  { value: 'incomplete_data', label: 'Incomplete Data' },
  { value: 'suspicious_activity', label: 'Suspicious Activity' },
  { value: 'document_issues', label: 'Document Issues' },
  { value: 'regulatory_concern', label: 'Regulatory Concern' },
  { value: 'identity_mismatch', label: 'Identity Mismatch' },
  { value: 'other', label: 'Other' },
];

const ReviewDecisionModal: React.FC<ReviewDecisionModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  allSectionsReviewed,
}) => {
  const [selectedDecision, setSelectedDecision] = useState<ReviewDecision | null>(null);
  const [notes, setNotes] = useState('');
  const [rejectionCategory, setRejectionCategory] = useState<RejectionCategory | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, submitting]);

  // Simple focus trap: keep Tab within the modal
  const handleFocusTrap = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !modalRef.current) return;
    const focusable = modalRef.current.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  if (!isOpen) return null;

  const canSubmit =
    allSectionsReviewed &&
    selectedDecision !== null &&
    notes.trim().length > 0 &&
    (selectedDecision !== 'reject' || rejectionCategory !== undefined);

  const handleSubmit = async () => {
    if (!canSubmit || !selectedDecision) return;

    setSubmitting(true);
    try {
      await onSubmit(
        selectedDecision,
        notes.trim(),
        selectedDecision === 'reject' ? rejectionCategory : undefined
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setSelectedDecision(null);
      setNotes('');
      setRejectionCategory(undefined);
      onClose();
    }
  };

  const decisions: {
    value: ReviewDecision;
    label: string;
    icon: React.ElementType;
    color: string;
    bg: string;
    border: string;
    hoverBg: string;
  }[] = [
    {
      value: 'approve',
      label: 'Approve',
      icon: CheckCircle,
      color: 'text-green-700',
      bg: 'bg-green-50',
      border: 'border-green-300',
      hoverBg: 'hover:bg-green-100',
    },
    {
      value: 'changes_requested',
      label: 'Request Changes',
      icon: AlertTriangle,
      color: 'text-yellow-700',
      bg: 'bg-yellow-50',
      border: 'border-yellow-300',
      hoverBg: 'hover:bg-yellow-100',
    },
    {
      value: 'reject',
      label: 'Reject',
      icon: XCircle,
      color: 'text-red-700',
      bg: 'bg-red-50',
      border: 'border-red-300',
      hoverBg: 'hover:bg-red-100',
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="review-decision-modal-title"
        onKeyDown={handleFocusTrap}
        className="relative w-full max-w-lg mx-4 bg-white rounded-xl shadow-xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 id="review-decision-modal-title" className="text-lg font-semibold text-gray-900">Submit Review Decision</h3>
          <button
            onClick={handleClose}
            disabled={submitting}
            className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Warning if not all sections reviewed */}
          {!allSectionsReviewed && (
            <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  Not all sections reviewed
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  You must review all KYC sections before submitting a decision. Please go back and mark all sections as reviewed.
                </p>
              </div>
            </div>
          )}

          {/* Decision buttons */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Decision
            </label>
            <div className="grid grid-cols-3 gap-3">
              {decisions.map((d) => {
                const Icon = d.icon;
                const isSelected = selectedDecision === d.value;
                return (
                  <button
                    key={d.value}
                    onClick={() => setSelectedDecision(d.value)}
                    disabled={!allSectionsReviewed}
                    className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                      isSelected
                        ? `${d.bg} ${d.border} ${d.color}`
                        : `bg-white border-gray-200 text-gray-500 ${allSectionsReviewed ? d.hoverBg : ''}`
                    }`}
                  >
                    <Icon className={`w-6 h-6 ${isSelected ? d.color : ''}`} />
                    <span className="text-sm font-medium">{d.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Rejection category (only when reject selected) */}
          {selectedDecision === 'reject' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rejection Category <span className="text-red-500">*</span>
              </label>
              <select
                value={rejectionCategory || ''}
                onChange={(e) => setRejectionCategory(e.target.value as RejectionCategory)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <option value="">Select a category...</option>
                {REJECTION_CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Notes textarea */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Decision Notes <span className="text-red-500">*</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={
                selectedDecision === 'approve'
                  ? 'Confirm all checks passed and data is complete...'
                  : selectedDecision === 'changes_requested'
                  ? 'Describe what changes are needed...'
                  : selectedDecision === 'reject'
                  ? 'Provide detailed reason for rejection...'
                  : 'Select a decision first, then add your notes...'
              }
              rows={4}
              disabled={!allSectionsReviewed}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
            />
            {notes.trim().length === 0 && selectedDecision && (
              <p className="text-xs text-red-500 mt-1">Notes are required</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
              selectedDecision === 'approve'
                ? 'bg-green-600 hover:bg-green-700'
                : selectedDecision === 'reject'
                ? 'bg-red-600 hover:bg-red-700'
                : selectedDecision === 'changes_requested'
                ? 'bg-yellow-600 hover:bg-yellow-700'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {selectedDecision === 'approve'
              ? 'Approve KYC'
              : selectedDecision === 'reject'
              ? 'Reject KYC'
              : selectedDecision === 'changes_requested'
              ? 'Request Changes'
              : 'Submit Decision'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewDecisionModal;
